import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiLock, FiPhone, FiEye, FiEyeOff, FiCheck, FiArrowLeft } from 'react-icons/fi';
import { sendOTP, registerUser } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function RegisterPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [step,      setStep]      = useState(1);
  const [form,      setForm]      = useState({ fullName:'', email:'', password:'', phone:'', collegeName:'', yearOfStudy:'' });
  const [otp,       setOtp]       = useState(['','','','','','']);
  const [showPass,  setShowPass]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [errors,    setErrors]    = useState({});
  const [countdown, setCountdown] = useState(0);
  const otpRefs  = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setTimeout(() => setCountdown(c => c - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [countdown]);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(er => ({ ...er, [e.target.name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim())               e.fullName = 'Please enter your full name';
    if (!form.email)                          e.email    = 'Please enter your email';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email  = 'Enter a valid email (e.g. you@gmail.com)';
    if (!form.password)                       e.password = 'Please enter a password';
    else if (form.password.length < 6)        e.password = 'Password must be at least 6 characters';
    return e;
  };

  const handleSendOTP = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await sendOTP({ email: form.email, fullName: form.fullName });
      toast.success('OTP sent to ' + form.email + ' ✅');
      setStep(2);
      setCountdown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 300);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      toast.error(msg);
      if (msg.toLowerCase().includes('email')) setErrors({ email: msg });
    } finally { setLoading(false); }
  };

  const handleOtpChange = (i, val) => {
    const digit = val.replace(/\D/, '');
    const newOtp = [...otp];
    newOtp[i] = digit;
    setOtp(newOtp);
    if (digit && i < 5) otpRefs.current[i + 1]?.focus();
    if (digit && i === 5) {
      const full = [...newOtp.slice(0,5), digit].join('');
      if (full.length === 6) handleVerifyOTP(full);
    }
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
    if (e.key === 'Enter') { const full = otp.join(''); if (full.length === 6) handleVerifyOTP(full); }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (pasted.length === 6) { setOtp(pasted.split('')); otpRefs.current[5]?.focus(); handleVerifyOTP(pasted); }
  };

  const handleVerifyOTP = async (otpStr) => {
    const code = otpStr || otp.join('');
    if (code.length !== 6) { toast.error('Enter all 6 digits'); return; }
    setLoading(true);
    try {
      const res = await registerUser({ ...form, otp: code });
      login(res.data.token, res.data.user);
      toast.success('Account created! Welcome to Korpnex 🎉');
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.message || 'Verification failed.';
      toast.error(msg);
      if (msg.toLowerCase().includes('incorrect') || msg.toLowerCase().includes('wrong')) {
        setOtp(['','','','','','']);
        otpRefs.current[0]?.focus();
      }
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setLoading(true);
    setOtp(['','','','','','']);
    try {
      await sendOTP({ email: form.email, fullName: form.fullName });
      toast.success('New OTP sent! ✅');
      setCountdown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 200);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to resend OTP'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', padding:'80px 16px 32px' }}>
      <style>{`
        .auth-card { background:white; border-radius:var(--radius-xl); box-shadow:var(--shadow-lg); padding:44px; width:100%; max-width:520px; border:1px solid var(--border); }
        .auth-logo { text-align:center; margin-bottom:28px; }
        .auth-logo-icon { width:52px; height:52px; background:linear-gradient(135deg,var(--primary),var(--accent)); border-radius:14px; display:flex; align-items:center; justify-content:center; color:white; font-size:26px; font-weight:800; margin:0 auto 12px; }
        .auth-input-wrap { position:relative; }
        .auth-input-icon { position:absolute; left:14px; top:50%; transform:translateY(-50%); color:var(--mid); pointer-events:none; }
        .auth-input { padding-left:42px !important; }
        .auth-input-toggle { position:absolute; right:14px; top:50%; transform:translateY(-50%); color:var(--mid); cursor:pointer; background:none; border:none; display:flex; }
        .step-bar { display:flex; align-items:center; margin-bottom:28px; }
        .step-dot { width:32px; height:32px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex-shrink:0; }
        .step-dot.active { background:var(--primary); color:white; }
        .step-dot.done { background:var(--success); color:white; }
        .step-dot.pending { background:var(--border); color:var(--mid); }
        .step-line { flex:1; height:2px; background:var(--border); }
        .step-line.done { background:var(--success); }
        .otp-inputs { display:flex; gap:10px; justify-content:center; margin:24px 0; }
        .otp-input { width:48px; height:56px; border:2px solid var(--border); border-radius:10px; text-align:center; font-size:24px; font-weight:700; color:var(--dark); background:white; transition:var(--transition); outline:none; }
        .otp-input:focus { border-color:var(--primary); box-shadow:0 0 0 3px rgba(37,99,235,0.12); }
        .otp-input.filled { border-color:var(--primary); background:var(--primary-light); }
      `}</style>

      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">K</div>
          <h2>{step === 1 ? 'Create Your Account' : 'Verify Your Email'}</h2>
          <p style={{ color:'var(--mid)', fontSize:14 }}>
            {step === 1 ? 'Join thousands of students building their tech careers' : 'Enter the 6-digit OTP sent to ' + form.email}
          </p>
        </div>

        {/* Step bar */}
        <div className="step-bar">
          <div className={`step-dot ${step > 1 ? 'done' : 'active'}`}>{step > 1 ? <FiCheck size={14}/> : '1'}</div>
          <div className={`step-line ${step > 1 ? 'done' : ''}`} />
          <div className={`step-dot ${step === 2 ? 'active' : 'pending'}`}>2</div>
        </div>

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <form onSubmit={handleSendOTP} noValidate>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name <span style={{color:'red'}}>*</span></label>
                <div className="auth-input-wrap">
                  <FiUser size={16} className="auth-input-icon"/>
                  <input name="fullName" value={form.fullName} onChange={handleChange}
                    className={`form-control auth-input${errors.fullName?' error':''}`} placeholder="Rahul Sharma"/>
                </div>
                {errors.fullName && <div className="form-error">{errors.fullName}</div>}
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <div className="auth-input-wrap">
                  <FiPhone size={16} className="auth-input-icon"/>
                  <input name="phone" value={form.phone} onChange={handleChange}
                    className="form-control auth-input" placeholder="+91 98765 43210"/>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Email Address <span style={{color:'red'}}>*</span></label>
              <div className="auth-input-wrap">
                <FiMail size={16} className="auth-input-icon"/>
                <input name="email" type="email" value={form.email} onChange={handleChange}
                  className={`form-control auth-input${errors.email?' error':''}`} placeholder="you@gmail.com"/>
              </div>
              {errors.email
                ? <div className="form-error">{errors.email}</div>
                : <div style={{fontSize:11,color:'var(--mid)',marginTop:4}}>A 6-digit OTP will be sent to this email</div>}
            </div>
            <div className="form-group">
              <label>Password <span style={{color:'red'}}>*</span></label>
              <div className="auth-input-wrap">
                <FiLock size={16} className="auth-input-icon"/>
                <input name="password" type={showPass?'text':'password'} value={form.password} onChange={handleChange}
                  className={`form-control auth-input${errors.password?' error':''}`} placeholder="Minimum 6 characters"/>
                <button type="button" className="auth-input-toggle" onClick={() => setShowPass(s=>!s)}>
                  {showPass ? <FiEyeOff size={16}/> : <FiEye size={16}/>}
                </button>
              </div>
              {errors.password && <div className="form-error">{errors.password}</div>}
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>College / University</label>
                <input name="collegeName" value={form.collegeName} onChange={handleChange}
                  className="form-control" placeholder="Delhi University"/>
              </div>
              <div className="form-group">
                <label>Year of Study</label>
                <select name="yearOfStudy" value={form.yearOfStudy} onChange={handleChange} className="form-control">
                  <option value="">Select year</option>
                  {['1st Year','2nd Year','3rd Year','4th Year','Graduate','Other'].map(y=><option key={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary btn-lg"
              style={{width:'100%',justifyContent:'center',marginTop:8}}>
              {loading
                ? <><div className="spinner" style={{width:18,height:18,borderWidth:2}}/> Sending OTP...</>
                : '📧 Send Verification OTP'}
            </button>
          </form>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <div>
            <div style={{background:'var(--primary-light)',border:'1px solid #93c5fd',borderRadius:10,padding:'12px 16px',marginBottom:20,display:'flex',alignItems:'center',gap:10}}>
              <FiMail size={16} color="var(--primary)" style={{flexShrink:0}}/>
              <div style={{flex:1,fontSize:14}}>OTP sent to <strong>{form.email}</strong></div>
              <button onClick={() => {setStep(1); setOtp(['','','','','','']);}}
                style={{background:'none',border:'none',color:'var(--primary)',cursor:'pointer',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:4}}>
                <FiArrowLeft size={12}/> Change
              </button>
            </div>

            <div className="otp-inputs" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input key={i} ref={el => otpRefs.current[i]=el}
                  type="text" inputMode="numeric" maxLength={1} value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  className={`otp-input${digit?' filled':''}`}/>
              ))}
            </div>

            <button onClick={() => handleVerifyOTP()} disabled={loading || otp.join('').length !== 6}
              className="btn btn-primary btn-lg" style={{width:'100%',justifyContent:'center',marginBottom:16}}>
              {loading
                ? <><div className="spinner" style={{width:18,height:18,borderWidth:2}}/> Verifying...</>
                : <><FiCheck size={16}/> Verify &amp; Create Account</>}
            </button>

            <div style={{textAlign:'center',fontSize:14,color:'var(--mid)'}}>
              Didn't receive OTP?{' '}
              {countdown > 0
                ? <span style={{color:'var(--primary)',fontWeight:600}}>Resend in {countdown}s</span>
                : <button onClick={handleResend} disabled={loading}
                    style={{background:'none',border:'none',color:'var(--primary)',cursor:'pointer',fontWeight:600,fontSize:14}}>
                    Resend OTP
                  </button>}
            </div>

            <div style={{marginTop:16,background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#92400e'}}>
              📌 Check your spam/junk folder if you don't see the email. OTP expires in 10 minutes.
            </div>
          </div>
        )}

        {step === 1 && (
          <p style={{textAlign:'center',fontSize:14,color:'var(--mid)',marginTop:20}}>
            Already have an account?{' '}
            <Link to="/login" style={{color:'var(--primary)',fontWeight:600}}>Login here</Link>
          </p>
        )}
      </div>
    </div>
  );
}